'use client';

import React from 'react';
import Link from 'next/link';
import { Button, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { ArrowRight } from '@phosphor-icons/react/dist/ssr/ArrowRight';

import { Row } from '@/types/general';
import { useMediaQuery } from '@/hooks/use-media-query';
import { StatusChip } from '@/components/core/status-chip';

import { PlayerCell } from './player-cell';

export function HorizontalTable({
  header,
  status,
  tableData,
}: {
  header?: string;
  status?: string;
  tableData: Row[];
}): React.ReactNode {
  const isMobile = useMediaQuery('down', 'md');

  const entries = Object.entries(tableData);

  return (
    <Stack direction="column" gap={2}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        {header ? <Typography variant="h6">{header}</Typography> : null}
        {status ? <StatusChip status={status?.toUpperCase()} /> : null}
      </Stack>

      <Box
        sx={{
          overflow: 'hidden',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            border: '1px solid var(--mui-palette-divider)',
          }}
        >
          <TableBody>
            {entries.reduce((rows: React.JSX.Element[], [_, item], index) => {
              if (isMobile) {
                rows.push(
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell colSpan={2} sx={{ border: '1px solid var(--mui-palette-divider)', width: '100%' }}>
                      <Stack direction="row" gap={1}>
                        {item.label.text ? (
                          <Typography variant="body2" color="var(--mui-palette-text-secondary)" sx={{ flexShrink: 0 }}>
                            {item.label.text}:
                          </Typography>
                        ) : item.label.component ? (
                          item.label.component
                        ) : null}

                        <Box sx={{ width: 1, display: 'flex', flexDirection: 'row', alignItems: 'start' }}>
                          {item.value.text ? (
                            <Typography
                              variant="subtitle2"
                              color="var(--mui-palette-text-primary)"
                              sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {item.value.text}
                            </Typography>
                          ) : item.value.link ? (
                            <Button
                              component={Link}
                              href={item.value.link.href}
                              variant="contained"
                              endIcon={<ArrowRight />}
                              size="small"
                              sx={{ ml: 'auto' }}
                            >
                              {item.value.link.text || 'Open'}
                            </Button>
                          ) : item.value.player ? (
                            <PlayerCell
                              playerCode={item.value.player.playerCode}
                              playerId={item.value.player.playerId}
                              profile={item.value.player.profile}
                              isVerified={item.value.player.isVerified}
                              isTest={item.value.player.isTest}
                              username={item.value.player.username}
                              showCopyButton
                            />
                          ) : null}
                          {item.value.component ? item.value.component : null}
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              } else if (index % 2 === 0) {
                const [__, nextItem] = entries[index + 1] || [];
                rows.push(
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={index}>
                    <TableCell colSpan={2} sx={{ border: '1px solid var(--mui-palette-divider)', width: '50%' }}>
                      <Stack direction="row" gap={1}>
                        {item.label.text ? (
                          <Typography
                            variant="body2"
                            color="var(--mui-palette-text-secondary)"
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0 }}
                          >
                            {item.label.text}:
                          </Typography>
                        ) : item.label.component ? (
                          item.label.component
                        ) : null}

                        <Box sx={{ width: 1, display: 'flex', flexDirection: 'row', alignItems: 'start' }}>
                          {item.value.text ? (
                            <Typography
                              variant="subtitle2"
                              color="var(--mui-palette-text-primary)"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {item.value.text}
                            </Typography>
                          ) : item.value.link ? (
                            <Button
                              component={Link}
                              href={item.value.link.href}
                              variant="contained"
                              endIcon={<ArrowRight />}
                              size="small"
                              sx={{ ml: 'auto' }}
                            >
                              {item.value.link.text || 'Open'}
                            </Button>
                          ) : null}
                          {item.value.component ? item.value.component : null}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ border: '1px solid var(--mui-palette-divider)', width: '50%' }}>
                      {nextItem ? (
                        <Stack direction="row" gap={1}>
                          {nextItem.label.text ? (
                            <Typography
                              variant="body2"
                              color="var(--mui-palette-text-secondary)"
                              sx={{ flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {nextItem.label.text}:
                            </Typography>
                          ) : nextItem.label.component ? (
                            nextItem.label.component
                          ) : null}
                          <Box sx={{ width: 1, display: 'flex', flexDirection: 'row', alignItems: 'start' }}>
                            {nextItem.value.text ? (
                              <Typography
                                variant="subtitle2"
                                color="var(--mui-palette-text-primary)"
                                sx={{
                                  whiteSpace: 'nowrap',

                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {nextItem.value.text}
                              </Typography>
                            ) : nextItem.value.link ? (
                              <Button
                                component={Link}
                                href={nextItem.value.link.href}
                                variant="contained"
                                endIcon={<ArrowRight />}
                                size="small"
                                sx={{ ml: 'auto' }}
                              >
                                {nextItem.value.link.text || 'Open'}
                              </Button>
                            ) : null}
                            {nextItem.value.component ? nextItem.value.component : null}
                          </Box>
                        </Stack>
                      ) : (
                        ''
                      )}
                    </TableCell>
                  </TableRow>
                );
              }
              return rows;
            }, [])}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
}
